import { useRef, useState, useEffect } from 'react';
import Icon from '../AppIcon';

interface ComboBoxSearchOption {
  value: string;
  label: string;
}

interface ComboBoxSearchProps {
  label?: string;
  options: ComboBoxSearchOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
  compact?: boolean;
  icon?: string;
  dropupDirection?: boolean;
}

/**
 * Custom ComboBox component with search functionality
 * Includes an input field to filter options dynamically
 */
const ComboBoxSearch = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  className = '',
  disabled = false,
  id = 'combobox-search',
  compact = false,
  icon = 'ChevronDown',
  dropupDirection = false,
}: ComboBoxSearchProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get the currently selected option label
  const selectedOption = options.find(option => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  // Filter options based on search query
  const filteredOptions = options.filter(option => option.label.toLowerCase().includes(searchQuery.toLowerCase()));

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle option selection
  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  // Dynamic height based on compact mode
  const height = compact ? 'h-8' : 'h-9';
  const padding = compact ? 'px-2 py-1' : 'px-3 py-1.5';

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && <label className="block text-text-secondary text-xs mb-0.5">{label}</label>}
      <div
        className={`flex ${height} items-center justify-between w-full ${padding} border-gray-300 focus:ring-[#1E3A5F] border ${
          isOpen ? 'border-primary ring-1 ring-[#1E3A5F]' : 'border-border'
        } rounded cursor-pointer bg-white text-sm ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}`}
        onClick={toggleDropdown}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${id}-listbox`}
        id={id}
      >
        <div className={`flex-grow truncate ${selectedOption ? 'text-gray-900' : 'text-gray-400'}`}>{displayText}</div>
        <Icon
          name={icon}
          size={compact ? 14 : 16}
          className={`text-text-secondary flex-shrink-0 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {isOpen && (
        <div
          className={`absolute z-10 w-full bg-white border border-border rounded shadow-lg ${
            dropupDirection ? 'bottom-full mb-0.5' : 'top-full mt-0.5'
          }`}
          role="listbox"
          id={`${id}-listbox`}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Icon
                name="Search"
                size={14}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 text-text-secondary"
              />
              <input
                ref={searchInputRef}
                type="text"
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded focus:outline-none focus:ring-1 focus:ring-[#1E3A5F] focus:border-primary"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
              {searchQuery && (
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-text-secondary hover:text-text-primary"
                  onClick={e => {
                    e.stopPropagation();
                    setSearchQuery('');
                    searchInputRef.current?.focus();
                  }}
                  aria-label="Clear search"
                >
                  <Icon name="X" size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="py-2 px-3 text-text-secondary text-center text-sm">
                {searchQuery ? 'No results found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  className={`py-1.5 px-3 cursor-pointer hover:bg-primary/5 text-sm ${
                    value === option.value ? 'bg-primary/10 text-primary' : 'text-text-primary'
                  }`}
                  onClick={() => handleSelect(option.value)}
                  role="option"
                  aria-selected={value === option.value}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboBoxSearch;
